import { compareStringsForNaturalOrderSort } from './sort'

export interface ParsedStreetAddress {
  unitNumber: number | null
  streetNumber: number | null
  streetNumberSuffix: string | null
  streetNumberRangeEnd: number | null
  street: string
}

// Types of street addresses that determine the order of sorting
export enum StreetAddressType {
  REGULAR = 1,
  SUFFIXED = 2,
  UNIT = 3,
  RANGE = 4
}

export class StreetAddress {
  // Test pattern kept simple to maintain efficiency - must start with a number and include at least one space
  private static readonly streetAddressTestPattern = /^\d+.*\s/
  // Match pattern for street addresses - captures the number, suffix, separator, second number, and street name
  private static readonly streetAddressMatchPattern = /^(\d+)([A-Za-z]?)([\/\-]?)(\d*)\s*(.*)$/i

  // Returns true if the string looks like a street address
  static isStreetAddress(testStr: string): boolean {
    return this.streetAddressTestPattern.test(testStr.trim())
  }

  // Parses an address string into components
  static parse(addressStr: string): ParsedStreetAddress {
    const trimmedAddressStr = addressStr.trim()
    const match = this.streetAddressMatchPattern.exec(trimmedAddressStr)

    if (!match) {
      return { unitNumber: null, streetNumber: null, streetNumberSuffix: null, streetNumberRangeEnd: null, street: trimmedAddressStr }
    }

    const firstNumber = parseInt(match[1], 10)
    const streetNumberSuffix = match[2] || null
    const separator = match[3] || ''
    const secondNumber = match[4] ? parseInt(match[4], 10) : null
    const street = match[5]?.trim() ?? ''

    let unitNumber: number | null = null
    let streetNumber: number | null = firstNumber
    let streetNumberRangeEnd: number | null = null

    if (separator === '/') {
      // Unit: 2/1 => unitNumber=2, streetNumber=1
      unitNumber = firstNumber
      streetNumber = secondNumber ?? firstNumber
    } else if (separator === '-') {
      // Range: 2-5
      streetNumberRangeEnd = secondNumber
    }

    return { unitNumber, streetNumber, streetNumberSuffix, streetNumberRangeEnd, street }
  }

  // Compare two street addresses for sorting
  static compare(addressA: string, addressB: string): number {
    const parsedAddressA = StreetAddress.parse(addressA)
    const parsedAddressB = StreetAddress.parse(addressB)

    // First compare the street numbers
    const streetNumberA = parsedAddressA.streetNumber ?? 0
    const streetNumberB = parsedAddressB.streetNumber ?? 0
    if (streetNumberA !== streetNumberB) {
      return streetNumberA - streetNumberB
    }

    // Next compare the types of street addresses: regular < suffixed < unit < range
    const streetAddressTypeA = StreetAddress.getStreetAddressType(parsedAddressA)
    const streetAddressTypeB = StreetAddress.getStreetAddressType(parsedAddressB)
    if (streetAddressTypeA !== streetAddressTypeB) {
      return streetAddressTypeA - streetAddressTypeB
    }

    // Next if the street address is not regular, compare the suffix, unit, or range
    if (streetAddressTypeA === StreetAddressType.SUFFIXED) {
      return (parsedAddressA.streetNumberSuffix ?? '').localeCompare(parsedAddressB.streetNumberSuffix ?? '')
    }
    if (streetAddressTypeA === StreetAddressType.UNIT) {
      return (parsedAddressA.unitNumber ?? 0) - (parsedAddressB.unitNumber ?? 0)
    }
    if (streetAddressTypeA === StreetAddressType.RANGE) {
      return (parsedAddressA.streetNumber ?? 0) - (parsedAddressB.streetNumber ?? 0)
    }

    // Finally, compare the street names
    return compareStringsForNaturalOrderSort(parsedAddressA.street, parsedAddressB.street)
  }

  // Determines the type of street address for sorting
  private static getStreetAddressType(parsedStreetAddress: ParsedStreetAddress): StreetAddressType {
    // Early exit for the most common case (regular addresses)
    if (!parsedStreetAddress.streetNumberSuffix && !parsedStreetAddress.unitNumber && !parsedStreetAddress.streetNumberRangeEnd) {
      return StreetAddressType.REGULAR
    }
    if (parsedStreetAddress.streetNumberSuffix) {
      return StreetAddressType.SUFFIXED
    }
    if (parsedStreetAddress.unitNumber) {
      return StreetAddressType.UNIT
    }

    return StreetAddressType.RANGE
  }
}
